import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useMediaQuery,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { type JSX, useState } from 'react';

import { hasPermission } from '../../../auth/has-permission';
import { useCurrentUser } from '../../../auth/use-current-user';
import {
  createRole,
  deleteRole,
  isCatalogRoleCode,
  listPermissions,
  listRoles,
  updateRole,
  type Role,
  type UpdateRoleInput,
} from '../../../roles/api';
import { groupPermissionsByResource, type PermissionGroup } from '../../../roles/permission-groups';
import { mapRoleError } from '../../../roles/roles-api-errors';
import { RoleFormModal, type RoleFormSubmitValues } from './components/RoleFormModal';

type ModalState = { mode: 'create' } | { mode: 'edit'; role: Role } | null;

/**
 * `/settings/roles` — `settings-hub/spec.md` "Roles y Permisos distingue
 * matriz de solo lectura de gestión". A single view, no tabs (design.md
 * §9.5): the read-only matrix and the management controls coexist, gated by
 * `role.read`/`role.manage`.
 */
export function RolesPage(): JSX.Element {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const [modal, setModal] = useState<ModalState>(null);
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);

  const canManage = hasPermission(currentUser.data, 'role.manage');

  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: listRoles });
  const permissionsQuery = useQuery({ queryKey: ['roles', 'permissions'], queryFn: listPermissions });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['roles'] });
  }

  const createMutation = useMutation({
    mutationFn: createRole,
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Rol creado');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRoleInput }) => updateRole(id, input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Rol actualizado');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteRole,
    onMutate: () => setDeleteError(undefined),
    onSuccess: async () => {
      await invalidate();
      toast('success', 'Rol eliminado');
    },
    onError: (error) => setDeleteError(mapRoleError(error)),
  });

  const formError =
    modal?.mode === 'create'
      ? mapRoleError(createMutation.error)
      : modal?.mode === 'edit'
        ? mapRoleError(updateMutation.error)
        : undefined;

  const isLoading = rolesQuery.isPending || permissionsQuery.isPending;
  const hasError = rolesQuery.isError || permissionsQuery.isError;

  const groups = groupPermissionsByResource(permissionsQuery.data ?? []);

  function confirmDelete(role: Role): void {
    if (window.confirm(`¿Eliminar el rol "${role.name}"? Esta acción no se puede deshacer.`)) {
      deleteMutation.mutate(role.id);
    }
  }

  async function handleSubmit(values: RoleFormSubmitValues): Promise<void> {
    if (modal?.mode === 'create') {
      await createMutation.mutateAsync({
        code: values.code ?? '',
        name: values.name,
        ...(values.description === undefined ? {} : { description: values.description }),
        permissionCodes: values.permissionCodes,
      });
      return;
    }

    if (modal?.mode === 'edit') {
      await updateMutation.mutateAsync({
        id: modal.role.id,
        input: {
          name: values.name,
          description: values.description ?? null,
          permissionCodes: values.permissionCodes,
        },
      });
    }
  }

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={ShieldCheck}
        title="Roles y Permisos"
        description="Consultá la matriz de roles y sus permisos."
        actions={
          canManage ? (
            <Button icon={Plus} onClick={() => setModal({ mode: 'create' })}>
              Nuevo rol
            </Button>
          ) : undefined
        }
      />

      {deleteError !== undefined ? <Alert tone="danger">{deleteError}</Alert> : null}

      {hasError ? (
        <Alert tone="danger" title="No pudimos cargar los roles">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void rolesQuery.refetch();
                void permissionsQuery.refetch();
              }}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {!isLoading && rolesQuery.data !== undefined && rolesQuery.data.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="Todavía no hay roles." description="" />
      ) : null}

      {!isLoading && rolesQuery.data !== undefined && rolesQuery.data.length > 0 ? (
        isDesktop ? (
          <RolesMatrixTable
            roles={rolesQuery.data}
            groups={groups}
            canManage={canManage}
            onEdit={(role) => setModal({ mode: 'edit', role })}
            onDelete={confirmDelete}
          />
        ) : (
          <RolesMatrixCards
            roles={rolesQuery.data}
            groups={groups}
            canManage={canManage}
            onEdit={(role) => setModal({ mode: 'edit', role })}
            onDelete={confirmDelete}
          />
        )
      ) : null}

      {modal !== null ? (
        <RoleFormModal
          mode={modal.mode}
          {...(modal.mode === 'edit' ? { role: modal.role } : {})}
          permissions={permissionsQuery.data ?? []}
          actorPermissionCodes={currentUser.data?.permissions ?? []}
          submitting={createMutation.isPending || updateMutation.isPending}
          {...(formError === undefined || formError === '' ? {} : { formError })}
          onCancel={() => setModal(null)}
          onSubmit={handleSubmit}
        />
      ) : null}
    </section>
  );
}

interface RolesMatrixProps {
  roles: Role[];
  groups: PermissionGroup[];
  canManage: boolean;
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
}

/** Desktop (≥640px): every permission is a column, headed by its resource group. */
function RolesMatrixTable({ roles, groups, canManage, onEdit, onDelete }: RolesMatrixProps): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <Table>
        <caption className="sr-only">Matriz de roles y permisos</caption>
        <thead>
          <TableRow>
            <TableHeader>Rol</TableHeader>
            {groups.map((group) => (
              <TableHeader key={group.resource} colSpan={group.permissions.length}>
                {group.label}
              </TableHeader>
            ))}
            {canManage ? <TableHeader>Acciones</TableHeader> : null}
          </TableRow>
          <TableRow>
            <TableHeader> </TableHeader>
            {groups.flatMap((group) =>
              group.permissions.map((permission) => (
                <TableHeader key={permission.code}>
                  <span title={permission.description}>{permission.description}</span>
                </TableHeader>
              )),
            )}
            {canManage ? <TableHeader> </TableHeader> : null}
          </TableRow>
        </thead>
        <TableBody>
          {roles.map((role) => {
            const isCatalog = isCatalogRoleCode(role.code);
            const permissionSet = new Set(role.permissions);

            return (
              <TableRow key={role.id}>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <span className="font-medium text-slate-900">{role.name}</span>
                    {isCatalog ? <Badge variant="neutral">Rol del sistema</Badge> : null}
                  </div>
                </TableCell>
                {groups.flatMap((group) =>
                  group.permissions.map((permission) => (
                    <TableCell key={permission.code}>
                      {permissionSet.has(permission.code) ? '●' : '—'}
                    </TableCell>
                  )),
                )}
                {canManage ? (
                  <TableCell>
                    {isCatalog ? null : (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" icon={Pencil} onClick={() => onEdit(role)}>
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Trash2}
                          disabled={role.userCount > 0}
                          title={
                            role.userCount > 0
                              ? 'No se puede eliminar: el rol tiene usuarios asignados.'
                              : undefined
                          }
                          onClick={() => onDelete(role)}
                        >
                          Eliminar
                        </Button>
                      </div>
                    )}
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/** Mobile (<640px): one card per role, permissions grouped by resource — never a horizontally scrolling matrix. */
function RolesMatrixCards({ roles, groups, canManage, onEdit, onDelete }: RolesMatrixProps): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {roles.map((role) => {
        const isCatalog = isCatalogRoleCode(role.code);
        const permissionSet = new Set(role.permissions);

        return (
          <Card key={role.id}>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <span className="font-medium text-slate-900">{role.name}</span>
                {isCatalog ? <Badge variant="neutral">Rol del sistema</Badge> : null}
              </div>

              {groups.map((group) => (
                <div key={group.resource} className="flex flex-col gap-1">
                  <p className="text-xs font-semibold text-slate-600">{group.label}</p>
                  <ul className="flex flex-col gap-0.5 text-sm text-slate-700">
                    {group.permissions.map((permission) => (
                      <li key={permission.code} className="flex items-center gap-2">
                        <span aria-hidden="true">{permissionSet.has(permission.code) ? '●' : '—'}</span>
                        <span>{permission.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {canManage && !isCatalog ? (
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" icon={Pencil} onClick={() => onEdit(role)}>
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Trash2}
                    disabled={role.userCount > 0}
                    title={
                      role.userCount > 0
                        ? 'No se puede eliminar: el rol tiene usuarios asignados.'
                        : undefined
                    }
                    onClick={() => onDelete(role)}
                  >
                    Eliminar
                  </Button>
                </div>
              ) : null}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
